import { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthGate } from './views/AuthGate';
import { MembersView } from './views/MembersView';
import { SubscriptionView } from './views/SubscriptionView';
import { readResetContext, readSsoError, useAuth } from './lib/useAuth';
import { fetchMeta, fetchStats } from './api';
import { FilterBar } from './components/FilterBar';
import { formatNumber, relativeTime } from './lib/format';
import { readStoredFilters, writeStoredFilters } from './lib/storedFilters';
import { useTheme } from './lib/useTheme';
import { AreaView } from './views/AreaView';
import { AssignmentsView } from './views/AssignmentsView';
import { OverviewView } from './views/OverviewView';
import { TechView } from './views/TechView';
import { TrendView } from './views/TrendView';
import { EMPTY_FILTERS, type AggregateBundle, type Filters, type Meta } from './types';

type TabId = 'oversikt' | 'trender' | 'tekniker' | 'omraden' | 'uppdrag' | 'bevakning' | 'medlemmar';

const TABS: { id: TabId; label: string }[] = [
  { id: 'oversikt', label: 'Översikt' },
  { id: 'omraden', label: 'Områden' },
  { id: 'tekniker', label: 'Tekniker' },
  { id: 'uppdrag', label: 'Uppdrag' },
  { id: 'trender', label: 'Trender' },
  { id: 'bevakning', label: 'Bevakning' },
];

/** Bara administratörer ser medlemsfliken. */
const ADMIN_TAB: { id: TabId; label: string } = { id: 'medlemmar', label: 'Medlemmar' };

export default function App() {
  const auth = useAuth();
  const [theme, setTheme] = useTheme();
  const [tab, setTab] = useState<TabId>(() => (readHashTab() ?? 'oversikt'));
  const [filters, setFilters] = useState<Filters>(readStoredFilters);
  const [meta, setMeta] = useState<Meta>();
  const [stats, setStats] = useState<AggregateBundle>();
  const [baseline, setBaseline] = useState<AggregateBundle>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  // Läses en gång vid start. Adressfältet ändras medan appen kör, och då är
  // token borta.
  const [resetContext] = useState(readResetContext);
  const [ssoError] = useState(readSsoError);

  useEffect(() => {
    if (!auth.user) return;
    const controller = new AbortController();
    Promise.all([fetchMeta(controller.signal), fetchStats(EMPTY_FILTERS, controller.signal)])
      .then(([metaResult, statsResult]) => {
        setMeta(metaResult);
        setBaseline(statsResult);
      })
      .catch((cause: Error) => {
        if (cause.name !== 'AbortError') setError(cause.message);
      });
    return () => controller.abort();
  }, [auth.user]);

  useEffect(() => {
    if (!auth.user) return;
    const controller = new AbortController();
    setLoading(true);
    fetchStats(filters, controller.signal)
      .then((result) => {
        setStats(result);
        setError(undefined);
      })
      .catch((cause: Error) => {
        if (cause.name !== 'AbortError') setError(cause.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [filters, auth.user]);

  useEffect(() => writeStoredFilters(filters), [filters]);

  // Bara inloggad. Annars skriver den över återställningslänkens token med
  // "#oversikt" innan inloggningsvyn hunnit läsa den, och länken från mejlet
  // landar på en vanlig inloggningsruta.
  useEffect(() => {
    if (auth.user) window.location.hash = tab;
  }, [tab, auth.user]);

  const patchFilters = useCallback((patch: Partial<Filters>) => setFilters((current) => ({ ...current, ...patch })), []);

  /** Tar med sig ett urval till Uppdrag-fliken och byter dit. */
  const showAssignments = useCallback(
    (patch: Partial<Filters>) => {
      patchFilters(patch);
      setTab('uppdrag');
    },
    [patchFilters],
  );

  // Årslistan och teknikordlistan hämtas från ofiltrerad data, annars skulle
  // menyerna krympa så fort man valt något och man kan inte ta sig tillbaka.
  const availableYears = useMemo(
    () => [...new Set((baseline?.months ?? []).map((m) => Number(m.slice(0, 4))))].sort((a, b) => b - a),
    [baseline],
  );
  const allTechnologies = useMemo(() => (baseline?.technologies ?? []).map((t) => t.name), [baseline]);
  // Orterna kommer från ofiltrerad data av samma skäl som åren: listan ska inte
  // krympa så fort man valt något.
  const availableLocations = useMemo(() => (baseline?.topLocations ?? []).slice(0, 25), [baseline]);

  const monthsWithData = stats?.months.length ?? 0;
  const perMonth = monthsWithData > 0 ? Math.round((stats?.totalAssignments ?? 0) / monthsWithData) : 0;
  const failedSources = meta?.lastRun?.results.filter((r) => !r.ok) ?? [];

  if (auth.checking) {
    return (
      <div className="auth">
        <div className="skeleton" style={{ width: 430, height: 380 }} />
      </div>
    );
  }

  if (!auth.user) {
    return (
      <AuthGate
        providers={auth.providers}
        {...(ssoError ? { ssoError } : {})}
        onAuthenticated={(user) => {
          auth.setUser(user);
          // Rensa återställningstoken ur adressfältet så att den inte ligger
          // kvar i historiken eller delas av misstag.
          if (resetContext) window.location.hash = 'oversikt';
        }}
        {...(resetContext ? { resetContext } : {})}
      />
    );
  }

  const visibleTabs = auth.user.role === 'admin' ? [...TABS, ADMIN_TAB] : TABS;

  // Bevakning och Medlemmar rör inte urvalet. Med filterraden och KPI-korten
  // kvar såg det ut som att en bevakning ärvde filtret – t.ex. att man bara
  // skulle få uppdrag från den mäklare man råkat filtrera på.
  const showsData = tab !== 'bevakning' && tab !== 'medlemmar';

  return (
    <div className="app">
      <header className="masthead">
        <div className="masthead__inner">
          <div className="brand">
            <RadarMark />
            <div>
              <h1 className="brand__title">Uppdragsradar</h1>
              <p className="brand__subtitle">Vad marknaden efterfrågar – per område och teknik</p>
            </div>
          </div>

          <div className="masthead__meta">
            {meta?.lastRun ? (
              <>
                Uppdaterad {relativeTime(meta.lastRun.finishedAt)}
                <br />
                {formatNumber(baseline?.totalAssignments ?? 0)} uppdrag totalt
              </>
            ) : (
              'Hämtar status…'
            )}
          </div>

          <div className="account">
            <span className="account__name">{auth.user.displayName}</span>
            <button type="button" className="link-button" onClick={() => void auth.signOut()}>
              Logga ut
            </button>
          </div>

          <button
            type="button"
            className="icon-button"
            onClick={() => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')}
            title={`Tema: ${theme === 'system' ? 'följer systemet' : theme === 'dark' ? 'mörkt' : 'ljust'}`}
          >
            <span aria-hidden="true">{theme === 'dark' ? '◐' : theme === 'light' ? '○' : '◑'}</span>
            <span className="visually-hidden">Byt tema</span>
          </button>
        </div>
      </header>

      <main className="shell">
        {showsData ? (
          <FilterBar
            filters={filters}
            onChange={setFilters}
            meta={meta}
            stats={baseline}
            availableYears={availableYears}
            availableLocations={availableLocations}
            allTechnologies={allTechnologies}
          />
        ) : null}

        {error ? (
          <div className="notice">
            <span aria-hidden="true">⚠</span>
            <span>
              Kunde inte hämta data från API:t: {error}. Kontrollera att Function App:en kör och att lagringen är
              åtkomlig.
            </span>
          </div>
        ) : null}

        {failedSources.length > 0 ? (
          <div className="notice">
            <span aria-hidden="true">⚠</span>
            <span>
              Senaste insamlingen misslyckades för {failedSources.map((f) => f.source).join(', ')}. Siffrorna för de
              källorna kan vara inaktuella.
            </span>
          </div>
        ) : null}

        {showsData ? (
          <div className="kpi-row">
            <Kpi label="Uppdrag i urvalet" value={formatNumber(stats?.totalAssignments ?? 0)} hint={`${monthsWithData} månader med data`} />
            <Kpi label="Unika tekniker" value={formatNumber(stats?.technologies.length ?? 0)} hint="Utlästa ur annonstexterna" />
            <Kpi label="Snitt per månad" value={formatNumber(perMonth)} hint="Nya uppdrag" />
            <Kpi
              label="Aktiva källor"
              value={String(stats?.sources.filter((s) => s.count > 0).length ?? 0)}
              hint={`av ${meta?.sources.length ?? 0} bevakade`}
            />
          </div>
        ) : null}

        <nav className="tabs" role="tablist" aria-label="Vyer">
          {visibleTabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className="tab"
              aria-selected={tab === item.id}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              {item.id === 'tekniker' && stats ? <span className="tab__count">{stats.technologies.length}</span> : null}
              {item.id === 'uppdrag' && stats ? <span className="tab__count">{stats.totalAssignments}</span> : null}
            </button>
          ))}
        </nav>

        {!stats && loading ? (
          <div className="panel-grid panel-grid--split">
            <div className="skeleton" style={{ height: 320, gridColumn: '1 / -1' }} />
            <div className="skeleton" style={{ height: 260 }} />
            <div className="skeleton" style={{ height: 260 }} />
          </div>
        ) : stats ? (
          <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease' }}>
            {tab === 'oversikt' ? (
              <OverviewView
                stats={stats}
                filters={filters}
                meta={meta}
                onFilterChange={patchFilters}
                onShowAssignments={showAssignments}
                onShowTechnologies={() => setTab('tekniker')}
              />
            ) : null}
            {tab === 'trender' ? (
              <TrendView
                stats={stats}
                filters={filters}
                meta={meta}
                onFilterChange={patchFilters}
                onShowAssignments={showAssignments}
              />
            ) : null}
            {tab === 'tekniker' ? (
              <TechView
                stats={stats}
                filters={filters}
                meta={meta}
                onFilterChange={patchFilters}
                onShowAssignments={showAssignments}
              />
            ) : null}
            {tab === 'omraden' ? (
              <AreaView
                stats={stats}
                filters={filters}
                meta={meta}
                onFilterChange={patchFilters}
                onShowAssignments={showAssignments}
              />
            ) : null}
            {tab === 'uppdrag' ? <AssignmentsView filters={filters} meta={meta} onFilterChange={patchFilters} /> : null}
            {tab === 'bevakning' ? <SubscriptionView meta={meta} stats={baseline ?? stats} /> : null}
            {tab === 'medlemmar' && auth.user.role === 'admin' ? <MembersView currentUser={auth.user} meta={meta} /> : null}
          </div>
        ) : (
          <p className="empty">Ingen data ännu. Kör en backfill för att fylla på.</p>
        )}
      </main>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="kpi">
      <p className="kpi__label">{label}</p>
      <p className="kpi__value">{value}</p>
      <p className="kpi__hint">{hint}</p>
    </div>
  );
}

function RadarMark() {
  return (
    <svg className="brand__mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="14" stroke="var(--border-strong)" strokeWidth="1.5" />
      <circle cx="16" cy="16" r="8.5" stroke="var(--border-strong)" strokeWidth="1" />
      <circle cx="16" cy="16" r="3" stroke="var(--border-strong)" strokeWidth="1" />
      <path d="M16 16 L16 2 A14 14 0 0 1 28.1 9" fill="var(--accent)" opacity="0.22" />
      <line x1="16" y1="16" x2="16" y2="2" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="22.5" cy="11" r="2.2" fill="var(--series-2)" />
      <circle cx="11" cy="21" r="1.8" fill="var(--series-3)" />
    </svg>
  );
}

function readHashTab(): TabId | undefined {
  const hash = window.location.hash.replace('#', '');
  return TABS.some((t) => t.id === hash) ? (hash as TabId) : undefined;
}
